/**
 * The dialog card's own chrome — one spelling shared by every product app instead of a copy per
 * dialog. On phones the host collapses (`dlg-phone:contents`) and `hlm-dialog-sheet` draws the
 * drawer; on desktop the host IS the panel: width preset, radius, hairline, surface, padding, lift.
 * Consumed as `host: { '[class]': 'hostClass' }` — a static `class:` cannot call a function.
 */

/** Width presets: the `--dlg-w-*` token suffix, plus the two client dialogs sized on the spacing scale. */
const DIALOG_SHEET_WIDTH = {
  xs: 'dlg-desktop:w-[var(--dlg-w-xs)]',
  s: 'dlg-desktop:w-[var(--dlg-w-s)]',
  m: 'dlg-desktop:w-[var(--dlg-w-m)]',
  l: 'dlg-desktop:w-[var(--dlg-w-l)]',
  xl: 'dlg-desktop:w-[var(--dlg-w-xl)]',
  '2xl': 'dlg-desktop:w-[var(--dlg-w-2xl)]',
  '4xl': 'dlg-desktop:w-[var(--dlg-w-4xl)]',
  '5xl': 'dlg-desktop:w-[var(--dlg-w-5xl)]',
  bonus: 'dlg-desktop:w-[var(--dlg-w-bonus)]',
  person: 'dlg-desktop:w-[var(--dlg-w-person)]',
  project: 'dlg-desktop:w-[var(--dlg-w-project)]',
  'photo-picker': 'dlg-desktop:w-[var(--dlg-w-photo-picker)]',
  'w-105': 'dlg-desktop:w-105',
  'w-125': 'dlg-desktop:w-125',
  /** No width of its own: the dialog binds it on the host (`--dlg-w`), the preset only caps it at the viewport. */
  fluid: 'dlg-desktop:max-w-[var(--dlg-w-max)]',
} as const;

/**
 * Desktop card chrome — radius and hairline. `product` is the signed-in apps' card; `public` is the
 * softer guest-facing card of the public galleries (smaller radius, lighter line).
 */
const DIALOG_SHEET_CHROME = {
  product: 'dlg-desktop:rounded-[var(--r-xl)] dlg-desktop:border dlg-desktop:border-line2',
  public: 'dlg-desktop:rounded-[var(--r-l)] dlg-desktop:border dlg-desktop:border-line',
} as const;

/** Desktop padding: the three even steps, and the one dialog whose top/side/bottom air differs. */
const DIALOG_SHEET_PADDING = {
  'p-6': 'dlg-desktop:p-6',
  'p-8': 'dlg-desktop:p-8',
  'p-9': 'dlg-desktop:p-9',
  bonus: 'dlg-desktop:pt-7.5 dlg-desktop:px-8 dlg-desktop:pb-6.5',
} as const;

export type DialogSheetSize = keyof typeof DIALOG_SHEET_WIDTH;
export type DialogSheetPadding = keyof typeof DIALOG_SHEET_PADDING;
export type DialogSheetChrome = keyof typeof DIALOG_SHEET_CHROME;

export function dialogSheetHostClass(
  size: DialogSheetSize,
  padding: DialogSheetPadding = 'p-8',
  chrome: DialogSheetChrome = 'product',
): string {
  return (
    `block dlg-phone:contents ${DIALOG_SHEET_WIDTH[size]} ${DIALOG_SHEET_CHROME[chrome]} ` +
    `dlg-desktop:bg-panel ${DIALOG_SHEET_PADDING[padding]} dlg-desktop:shadow-[var(--shadow-4)]`
  );
}

/** Desktop host that caps at the viewport and only mediates: `hlm-dialog-sheet` inside stays the single scroller. */
export const DIALOG_SHEET_CAPPED_HOST = 'dlg-desktop:flex dlg-desktop:flex-col dlg-desktop:max-h-[var(--max-h-dialog-viewport)]';

/** A dialog that takes the whole screen instead of a card: its own scroll, no panel chrome. */
export const FULLSCREEN_DIALOG_HOST = 'relative flex h-screen w-screen flex-col overflow-hidden bg-bg2 text-ink';

/** The dialog's own title line — `brnDialogTitle` carries the semantics, this carries the look. */
export const DIALOG_TITLE_CLASS = 'mb-3 text-lg leading-snug font-bold text-ink';

/** Title and lead of the compact `bonus` card (Telegram contact, newcomer welcome) — the display heading over one plain line. */
export const DIALOG_CARD_TITLE_CLASS =
  'mb-2.5 text-xl font-semibold leading-[var(--leading-heading)] tracking-[var(--ls-display)] text-ink';
export const DIALOG_CARD_LEAD_CLASS = 'mb-5 text-base leading-normal text-ink';

/** The spinner a dialog's confirm button shows while the request is in flight. */
export const DIALOG_BUTTON_SPINNER_CLASS = 'size-4 animate-spin rounded-full border-2 border-current border-t-transparent';
